import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map, Observable, tap } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { User } from '../models/user.model';
import { UserStore } from '../stores/user.store';
import { AppleLoginRequest } from '../models/apple-auth.model';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  password: string;
}

export type VerifyCodePurpose = 'EMAIL_VERIFICATION' | 'PASSWORD_RESET';

export interface VerifyCodeRequest {
  email: string;
  code: string;
  purpose: VerifyCodePurpose;
}

export interface ResendCodeRequest {
  email: string;
  purpose: VerifyCodePurpose;
}

export interface AuthResponse {
  token?: string;
  user?: User;
}

export interface ApiDefaultResponse<T> {
  data: T;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private userStore = inject(UserStore);
  private readonly apiUrl = environment.apiUrl;

  login(credentials: LoginRequest): Observable<AuthResponse> {
    return this.http
      .post<ApiDefaultResponse<AuthResponse>>(`${this.apiUrl}/v1/auth/login`, credentials)
      .pipe(
        map((response) => response.data),
        tap((response) => {
          console.log('Login response:', response);
          if (response.token) {
            this.userStore.setToken(response.token);
            console.log('Token stored successfully');
          } else {
            console.warn('No token found in response:', response);
          }

          if (response.user) {
            this.userStore.setUser(response.user);
            console.log('User stored successfully');
          }
        }),
      );
  }

  register(data: RegisterRequest): Observable<AuthResponse> {
    return this.http
      .post<ApiDefaultResponse<AuthResponse>>(`${this.apiUrl}/v1/auth/register`, data)
      .pipe(
        map((response) => response.data),
        tap((response) => {
          if (response.token) {
            this.userStore.setToken(response.token);
          }

          if (response.user) {
            this.userStore.setUser(response.user);
          }
        }),
      );
  }

  loginWithFacebook(facebookAccessToken: string): Observable<AuthResponse> {
    const headers = new HttpHeaders({
      'facebook-token': facebookAccessToken,
    });
    return this.http
      .get<
        ApiDefaultResponse<AuthResponse>
      >(`${this.apiUrl}/v1/auth/facebook/callback`, { headers })
      .pipe(
        map((response) => response.data),
        tap((response) => {
          if (response.token) {
            this.userStore.setToken(response.token);
          }

          if (response.user) {
            this.userStore.setUser(response.user);
          }
        }),
      );
  }

  loginWithApple(payload: AppleLoginRequest): Observable<AuthResponse> {
    return this.http
      .post<ApiDefaultResponse<AuthResponse>>(`${this.apiUrl}/v1/auth/apple`, payload)
      .pipe(
        map((response) => response.data),
        tap((response) => {
          if (response.token) {
            this.userStore.setToken(response.token);
          }

          if (response.user) {
            this.userStore.setUser(response.user);
          }
        }),
      );
  }

  logout(): void {
    this.userStore.clear();
    this.router.navigate(['/auth/login']);
  }

  sendForgotPasswordMail(data: ForgotPasswordRequest): Observable<unknown> {
    return this.http.post<unknown>(`${this.apiUrl}/v1/auth/send-forgot-password-mail`, data);
  }

  /** Requests a fresh verification code for the "send again" flow (60s cooldown, matches API). */
  resendCode(data: ResendCodeRequest): Observable<unknown> {
    return this.http.post<unknown>(`${this.apiUrl}/v1/auth/resend-code`, data);
  }

  /**
   * Verifies the 4-digit code sent by email (forgot-password or email-confirmation flow).
   * On success the API logs the user in — the response carries a fresh auth token/user, which
   * we store just like `login()` does, so the following request (e.g. reset-password) is authorized.
   */
  verifyCode(data: VerifyCodeRequest): Observable<AuthResponse> {
    return this.http
      .post<ApiDefaultResponse<AuthResponse>>(`${this.apiUrl}/v1/auth/verify-code`, data)
      .pipe(
        map((response) => response.data),
        tap((response) => {
          if (response.token) {
            this.userStore.setToken(response.token);
          }

          if (response.user) {
            this.userStore.setUser(response.user);
          }
        }),
      );
  }

  resetPassword(data: ResetPasswordRequest): Observable<unknown> {
    return this.http.put<unknown>(`${this.apiUrl}/v1/auth/reset-password`, data);
  }
}
